/**
 * morphPipeline — fullscreen-quad raymarcher that morphs a 3D shape
 * between several SDF primitives with iridescent material and moving
 * light. The point is "cinematic" — a living, breathing, color-shifting
 * sculpture that the user can stare at for a while.
 *
 * Attribution: SDF primitives and rendering by Inigo Quilez, MIT licensed.
 *             Sin-based morphing and color shift are mine.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MorphPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// Fullscreen raymarcher. p = world position from camera ray, d = ray distance.
// Morphs between 5 SDF primitives in a 10-second cycle, smooth-min'd
// together for a continuous topology-shifting shape. Iridescent material
// shifts color along the surface using dot(N, V) as a hue modulator.
// Two moving light sources (one slow warm, one fast cool) create
// shifting highlights. Background is a deep radial nebula gradient that
// slowly swirls.
const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;

// ---------- IQ's SDF primitives (NSPP-flavored where needed) ----------
// sphere
float sdSphere(vec3 p, float r) { return length(p) - r; }
// box
float sdBox(vec3 p, vec3 b) {
  vec3 q = abs(p) - b;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}
// torus
float sdTorus(vec3 p, vec2 t) {
  vec2 q = vec2(length(p.xz) - t.x, p.y);
  return length(q) - t.y;
}
// octahedron
float sdOctahedron(vec3 p, float s) {
  p = abs(p);
  return (p.x + p.y + p.z - s) * 0.57735027;
}
// icosahedron-like 12-vertex "rounded polyhedron" (uses box+sphere blend)
float sdPolyhedron(vec3 p, float r, float s) {
  // Soft-blend of octahedron and rounded box. Gives a "soft crystal" feel.
  float o = sdOctahedron(p, s);
  float b = sdBox(p, vec3(s * 0.6));
  return mix(b, o, 0.5) - r;
}

// smooth min (Inigo Quilez) — for blending two SDFs
float smin(float a, float b, float k) {
  float h = max(k - abs(a - b), 0.0) / k;
  return min(a, b) - h * h * k * 0.25;
}

// Scene SDF: returns (distance, materialId) where materialId picks
// the per-shape color tint. We morph between 5 shapes.
struct Hit {
  float d;
  float mat;  // 0..1 — used as a "shape id" for color tinting
  vec3 local; // local position before the morph
};

Hit mapShapes(vec3 p) {
  // Slow rotation of the whole shape around two axes.
  float t = uTime * 0.18;
  float ca = cos(t), sa = sin(t);
  p.xz = mat2(ca, -sa, sa, ca) * p.xz;
  float cb = cos(t * 0.7), sb = sin(t * 0.7);
  p.yz = mat2(cb, -sb, sb, cb) * p.yz;

  // 5 shape "phases" cycle through. At any moment, the active shapes
  // are 2-3 of these blended together.
  float ph = fract(uTime * 0.08);  // 0..1 over 12.5s
  // Pick three shape weights: a, b, c with smooth transitions.
  // Shape 0 dominates ph=0..0.33, shape 1 at 0.33..0.66, shape 2 at 0.66..1.
  // We blend neighbors so the topology morphs continuously.
  float w0 = smoothstep(0.5, 0.0, abs(ph - 0.0));
  float w1 = smoothstep(0.5, 0.0, abs(ph - 0.33));
  float w2 = smoothstep(0.5, 0.0, abs(ph - 0.66));
  float sumW = w0 + w1 + w2 + 1e-4;
  w0 /= sumW; w1 /= sumW; w2 /= sumW;

  // Pulsing radius — the whole shape breathes.
  float pulse = 0.92 + 0.08 * sin(uTime * 0.6);
  float R = 0.7 * pulse;

  // Shape 0: pulsating sphere
  float d0 = sdSphere(p, R);
  Hit h0 = Hit(d0, 0.05, p);

  // Shape 1: torus
  float d1 = sdTorus(p, vec2(R, R * 0.32));
  Hit h1 = Hit(d1, 0.30, p);

  // Shape 2: rounded box
  float d2 = sdBox(p, vec3(R * 0.7)) - 0.08;
  Hit h2 = Hit(d2, 0.55, p);

  // Shape 3: octahedron
  float d3 = sdOctahedron(p, R * 1.05);
  Hit h3 = Hit(d3, 0.80, p);

  // Shape 4: icosahedron-ish
  float d4 = sdPolyhedron(p, 0.0, R * 1.0);
  Hit h4 = Hit(d4, 0.92, p);

  // Build the "target" weight vector: 5 weights summing to 1.0,
  // depending on the cycle phase. The first three get 60% of the weight
  // at any time, the other two get the remaining 40%.
  float w[5];
  w[0] = w0; w[1] = w1; w[2] = w2;
  w[3] = sin(uTime * 0.3) * 0.18 + 0.20;
  w[4] = sin(uTime * 0.27 + 1.3) * 0.18 + 0.20;

  Hit hs[5];
  hs[0] = h0; hs[1] = h1; hs[2] = h2; hs[3] = h3; hs[4] = h4;

  // Compute a weighted material id (for color tinting) and a smin-blended
  // distance. The trick: smin needs a single distance to march along, so
  // we use the *weighted* distance (smaller weight = smaller influence on
  // the blend). The material id is the weight-weighted average so the
  // color shifts as shapes morph.
  float d = 1e9;
  float mat = 0.0;
  for (int i = 0; i < 5; i++) {
    float wi = w[i];
    // smin: combine this shape's distance with the running minimum, with
    // a softness that scales with the weight (so rare shapes blend in
    // softly while dominant ones hold the silhouette).
    float k = 0.05 + 0.04 * (1.0 - wi);
    d = smin(d, hs[i].d, k);
    mat += wi * hs[i].mat;
  }

  return Hit(d, mat, p);
}

// SDF of the whole scene (no global marching needed — single shape).
float map(vec3 p) {
  return mapShapes(p).d;
}

// Numerical gradient → surface normal.
vec3 calcNormal(vec3 p) {
  const vec2 e = vec2(0.001, 0.0);
  return normalize(vec3(
    map(p + e.xyy) - map(p - e.xyy),
    map(p + e.yxy) - map(p - e.yxy),
    map(p + e.yyx) - map(p - e.yyx)
  ));
}

// Soft shadow: distance to the nearest occluder along a light ray.
float softShadow(vec3 ro, vec3 rd, float k) {
  float res = 1.0;
  float t = 0.05;
  for (int i = 0; i < 32; i++) {
    float h = map(ro + rd * t);
    res = min(res, k * h / t);
    t += clamp(h, 0.02, 0.4);
    if (h < 0.001) break;
  }
  return clamp(res, 0.0, 1.0);
}

// Background: deep radial gradient with a slow swirling noise.
vec3 background(vec3 rd) {
  // Center is (0, 0, 1) — i.e., looking toward the shape from the camera.
  // Use rd.y as the "depth" component.
  float t = uTime * 0.05;
  // Base dark gradient: deep blue at top, dark purple at bottom.
  vec3 base = mix(
    vec3(0.04, 0.02, 0.10),  // bottom
    vec3(0.02, 0.05, 0.12),  // top
    rd.y * 0.5 + 0.5
  );
  // Add a slow-swirling "nebula" — uses sin(rd * freq + t) modulated by
  // a coarse pattern. Cheap and looks like moving gas.
  float n1 = sin(rd.x * 6.0 + t * 1.3) * cos(rd.y * 4.0 - t * 0.7);
  float n2 = sin(rd.x * 11.0 - t * 0.9 + rd.z * 5.0);
  float nebula = (n1 * 0.5 + n2 * 0.3) * 0.5 + 0.5;
  vec3 nebColor = mix(
    vec3(0.15, 0.05, 0.30),  // deep magenta
    vec3(0.05, 0.20, 0.40),  // teal-blue
    nebula
  );
  // Concentrate the nebula near the center (where the shape is).
  float center = 1.0 - length(rd.xy) * 0.5;
  return base + nebColor * nebula * 0.3 * center;
}

// Iridescent material: thin-film color shift based on view angle.
vec3 iridescence(float NdotV) {
  // Map NdotV (0..1) to a hue cycle.
  float h = 0.65 - NdotV * 0.4 + uTime * 0.05;
  // Convert hue → RGB (cheap approximation).
  float r = 0.5 + 0.5 * cos(6.2831853 * (h + 0.0));
  float g = 0.5 + 0.5 * cos(6.2831853 * (h + 0.33));
  float b = 0.5 + 0.5 * cos(6.2831853 * (h + 0.66));
  return vec3(r, g, b);
}

void main() {
  vec2 uv = (vUV * uResolution - 0.5 * uResolution) / uResolution.y;

  // Two moving point lights around the shape.
  // Light 1: slow warm light orbiting close.
  float t1 = uTime * 0.4;
  vec3 L1 = vec3(cos(t1) * 2.5, 1.2 + sin(t1 * 0.7) * 0.6, sin(t1) * 2.5);
  vec3 C1 = vec3(1.0, 0.7, 0.4);  // warm

  // Light 2: faster cool light orbiting the other way.
  float t2 = -uTime * 0.55;
  vec3 L2 = vec3(cos(t2) * 3.2, -0.8 + sin(t2) * 0.4, sin(t2) * 3.2);
  vec3 C2 = vec3(0.3, 0.7, 1.0);  // cool

  // Camera: orbit slowly at radius 3.2, slight elevation.
  float camT = uTime * 0.12;
  vec3 ro = vec3(cos(camT) * 3.2, 0.4 + sin(camT * 0.5) * 0.3, sin(camT) * 3.2);
  vec3 ta = vec3(0.0);  // always look at the origin
  vec3 cw = normalize(ta - ro);
  vec3 cu = normalize(cross(cw, vec3(0.0, 1.0, 0.0)));
  vec3 cv = cross(cu, cw);
  vec3 rd = normalize(uv.x * cu + uv.y * cv + 1.4 * cw);

  // Raymarch.
  float td = 0.0;
  float matId = 0.0;
  vec3 hitP = ro;
  bool hit = false;
  for (int i = 0; i < 96; i++) {
    vec3 p = ro + rd * td;
    float d = map(p);
    if (d < 0.001) {
      hit = true;
      hitP = p;
      matId = mapShapes(p).mat;
      break;
    }
    td += d * 0.95;
    if (td > 12.0) break;
  }

  vec3 col;
  if (hit) {
    vec3 n = calcNormal(hitP);
    float NdotV = clamp(dot(n, -rd), 0.0, 1.0);

    // Base material color: iridescence tinted by the shape's "material id"
    // (changes as the shape morphs).
    vec3 irid = iridescence(NdotV + matId * 0.6);
    // Boost the saturation of the iridescence.
    irid = pow(irid, vec3(0.9));
    vec3 base = irid * 0.9;

    // Two-light lighting with soft shadows.
    vec3 l1 = normalize(L1 - hitP);
    vec3 l2 = normalize(L2 - hitP);
    float diff1 = max(dot(n, l1), 0.0);
    float diff2 = max(dot(n, l2), 0.0);
    float sh1 = softShadow(hitP + n * 0.01, l1, 8.0);
    float sh2 = softShadow(hitP + n * 0.01, l2, 8.0);

    // Specular (Blinn-Phong) — gives a sharp highlight per light.
    float spec1 = pow(max(dot(reflect(-l1, n), -rd), 0.0), 32.0);
    float spec2 = pow(max(dot(reflect(-l2, n), -rd), 0.0), 32.0);

    vec3 lit = base * (
      C1 * diff1 * sh1 * 1.3 +
      C2 * diff2 * sh2 * 1.0
    );
    lit += C1 * spec1 * sh1 * 0.7;
    lit += C2 * spec2 * sh2 * 0.5;

    // A subtle ambient from below (the "glow" of the nebula) so the
    // shape is never fully black.
    lit += base * 0.12;

    // Add a rim light at silhouette edges — picks up the iridescence
    // strongly and makes the shape pop.
    float rim = pow(1.0 - NdotV, 3.0);
    lit += irid * rim * 0.6;

    col = lit;
  } else {
    col = background(rd);
  }

  // Atmospheric haze: blend the background into the shape based on
  // ray distance. This makes the shape feel "in" the nebula, not pasted on.
  float fog = 1.0 - exp(-td * 0.08);
  col = mix(col, background(rd), fog * 0.45);

  // Subtle vignette to focus the eye.
  float vig = 1.0 - dot(uv, uv) * 0.5;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping (Reinhard) and gamma correction.
  col = col / (1.0 + col * 0.7);
  col = pow(col, vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function createMorphPipeline(gl: WebGL2RenderingContext): MorphPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,
     1, -1,
    -1,  1,
    -1,  1,
     1, -1,
     1,  1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
  };
}

export function drawMorph(
  gl: WebGL2RenderingContext,
  pipe: MorphPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
